// 実績 (2026-10-31 作者「ボス10個＋腕前を少し」・名前と説明は後で案を出す)。
//   ボス10個は「文章の鍵穴」= 倒すとそのボスの短い文が読める (11-01 作者)。 文はボスの背景が埋まってから書く。
//   ここが持つのは「いつ解く (条件)」と「Steam の実績の API 名 (id)」だけ。 画面に出す名前・説明は
//   Steamworks の管理画面に登録する物。
//   解いた id は消えない記録 (record.ts PersistentRecord.achievements) に刻み、 Steam 版なら
//   desktop/preload.cjs の window.steamBridge.activateAchievement へも渡す。 ブラウザ版は記録に刻むだけで、 画面には出さない。
//   腕前は2つ (ちょうどのパリィの数・千秋楽で終幕まで) のまま (11-01 作者)。
//   下書き印つきの進行 (稽古・一戦だけモード) は解かない = 呼ぶ側 (game.ts) が scratch で弾く。

import { BOSS_DEFINITIONS } from './bosses'
import type { PersistentRecord, RunTotals } from './record'
import type { Difficulty } from './types'

// 'nameless-shade' → 'BOSS_NAMELESS_SHADE'。 Steam の API 名は英大文字・数字・_ だけにする。
export function bossAchievementId(bossId: string): string {
  return `BOSS_${bossId.toUpperCase().replace(/[^A-Z0-9]+/g, '_')}`
}

export const JUST_PARRY_ACHIEVEMENT_ID = 'SKILL_JUST_PARRY_100'
export const JUST_PARRY_ACHIEVEMENT_COUNT = 100
export const SENSHURAKU_ACHIEVEMENT_ID = 'SKILL_SENSHURAKU_CLEAR'

export const ACHIEVEMENT_IDS: readonly string[] = [
  ...BOSS_DEFINITIONS.map((b) => bossAchievementId(b.id)),
  JUST_PARRY_ACHIEVEMENT_ID,
  SENSHURAKU_ACHIEVEMENT_ID,
]

// 解く (純関数)。 もう解いてある・知らない id は記録を変えない。 新しく解いた id を返す。
export function unlockAchievements(rec: PersistentRecord, ids: readonly string[]): { rec: PersistentRecord, unlocked: string[] } {
  const unlocked = ids.filter((id, i) => ACHIEVEMENT_IDS.includes(id) && !rec.achievements.includes(id) && ids.indexOf(id) === i)
  if (unlocked.length === 0) return { rec, unlocked }
  return { rec: { ...rec, achievements: [...rec.achievements, ...unlocked] }, unlocked }
}

// ボスを倒した時に解くもの。 lifetime = 完了した周 + 今の周の累計 (record.ts lifetimeTotals)。
export function achievementsOnBossCleared(bossId: string, lifetime: RunTotals): string[] {
  const out = [bossAchievementId(bossId)]
  if (lifetime.justParry >= JUST_PARRY_ACHIEVEMENT_COUNT) out.push(JUST_PARRY_ACHIEVEMENT_ID)
  return out
}

// 終幕まで通した時に解くもの。
export function achievementsOnRunCompleted(difficulty: Difficulty): string[] {
  return difficulty === 'senshuraku' ? [SENSHURAKU_ACHIEVEMENT_ID] : []
}

type SteamBridge = { activateAchievement?: (id: string) => unknown }

function steamBridge(): SteamBridge | null {
  const w = (globalThis as { window?: { steamBridge?: SteamBridge } }).window
  return w?.steamBridge ?? null
}

// Steam へ渡す。 Steam 版の外 (ブラウザ・テスト) では何もしない。 失敗しても遊びは止めない
//   (起動時の syncAchievementsToSteam が次に渡し直す)。
export function reportAchievementsToSteam(ids: readonly string[]): void {
  const bridge = steamBridge()
  if (!bridge?.activateAchievement) return
  for (const id of ids) {
    try {
      const r = bridge.activateAchievement(id) as { catch?: (f: () => void) => void } | undefined
      r?.catch?.(() => {})
    } catch {}
  }
}

// 起動時: 記録にあるのに Steam へ届いていない物を渡し直す (オフラインで解いた・前回の渡しが失敗した)。
//   Steam の activate は解いてある物に呼んでも何も起きないので、 全部渡してよい。
export function syncAchievementsToSteam(rec: PersistentRecord): void {
  reportAchievementsToSteam(rec.achievements.filter((id) => ACHIEVEMENT_IDS.includes(id)))
}
